import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildRole24ColumnKey,
  buildRole24RowColumnKeys,
  isRole24Empty,
  isRole24StaticallyRequired,
  isRole24ValidatingControl,
  pickRole24Schema,
  Role24Dimension,
  Role24Schema,
  Role24SchemaControl,
} from './role24-notes-related-party-transactions.rules';
import { differsAtCentPrecision } from '../validation-field-key.util';
import {
  activeDimensions,
  readSlice,
  resolveDimensionVisibility,
} from './dimension-visibility.util';
import { getDimensionVisibilityConfig } from './dimension-visibility-registry';

/**
 * Pure validator for Role 24 (Notes - Related Party Transactions).
 *
 * Mirrors the editor's
 * role24-750000-notes-related-party-transactions.component.ts
 * evaluateSheetStatus() surface:
 *
 *   1. **Non-table required fields** (isRole24StaticallyRequired === true
 *      AND NOT ui.tableColumn) — flagged when the corresponding flat
 *      sheetData key is empty. The component's evaluateSheetStatus() checks
 *      `formControl.invalid` for such fields, which for TextBlock rows is
 *      driven solely by Validators.required (no pattern / min / max on
 *      TextBlock controls in role 24).
 *
 *   2. **Table-row required columns** (isRole24StaticallyRequired === true
 *      AND ui.tableColumn) — for each validating row, each CurrentYear column
 *      key (Total + 7 related-party members) per ACTIVE dimension is flagged
 *      empty via buildRole24RowColumnKeys(). The active dimensions come from
 *      resolveActiveDimensions(): like role 23, role 24 hides the Consolidated
 *      table when the filing's Nature of Financial Statements is "Separate", so
 *      a Separate filing checks only the Separate dimension. A Consolidated
 *      filing (or an unknown/unset nature — the pre-subscription default)
 *      checks both. Without this gate, role 24 — which is on
 *      PURE_VERDICT_SHEETS — would over-report every empty Consolidated cell of
 *      the mandatory rows ([#f31], [#f32]) and publish a red cross on a table
 *      the editor no longer renders (Bug-A parity class, shared with role 23).
 *
 *   3. **Cross-sheet requiredIf** (Tier 4 specific) — when a control
 *      declares a `requiredIf: { sheet, qname, equals }` rule AND the
 *      referenced sheet's field matches the configured value, the control
 *      becomes required on top of its static requiredness. **Neither role-24
 *      schema declares a requiredIf today**: the MPERS one (on
 *      `ifrs-smes:AmountsPayableRelatedPartyTransactions`) had its
 *      `TypeOfCompany == "Company limited by shares"` CONDITION scrapped by
 *      the P.O. on 2026-08-19, leaving that control plainly `required: true`
 *      (mandatory on MPERS, optional on MFRS). The machinery is retained
 *      because a schema edit can reinstate a rule with no code change — so
 *      the orchestrator must
 *      still feed filingPrerequisites into context.crossSheetData for future schema
 *      edits.
 *
 * ---- Schema variant handling --------------------------------------------
 *
 * Role 24 ships TWO form JSONs (MFRS / MPERS). The component picks at mount
 * time from filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'];
 * the pure path gets the same value through context.accountingStandard
 * (assembled by ConversionValidatorService.buildContext from filingInfo).
 * Selection is delegated to pickRole24Schema so component and validator
 * stay DRY.
 *
 * ---- Table-row repetition -----------------------------------------------
 *
 * Data shape: FLAT sheetData keyed by
 *   {qname}[{dim}][CurrentYear]                  (Total column)
 * or
 *   {qname}:{Member}[{dim}][CurrentYear]         (per-member column)
 *
 * NOT an array-of-rows. The validator expands each schema row's qname into
 * the column key list via buildRole24RowColumnKeys() and probes the flat
 * map. This means no interface change to SheetValidator /
 * SheetValidationContext is required for role 24.
 *
 * ---- requiredIf shape note (role 24 vs role 20) -------------------------
 *
 * Role 20 declares cross-sheet conditional-required rules on
 * `requiredIfCrossSheet: { sheet, field, equals }` (note: `field`).
 * Role 24 declares them on `requiredIf: { sheet, qname, equals }` (note:
 * `qname`). Two independently evolved shapes in the schema JSONs; we keep
 * both alive rather than renormalising — see the rules file header.
 *
 * ---- Lazy requiredMap — why the pure validator doesn't carry one --------
 *
 * The component's `requiredMap` cache exists because isFieldRequired() is
 * called on every template change-detection tick for every cell (thousands
 * of reads per CD pass). Memoising keyed by qname, invalidating when the
 * dependent sheet changes, collapses that to one scan per requiredIf flip.
 *
 * The pure validator runs ONCE per sheet validation. Its "cache" would
 * live for the duration of a single validate() call and be thrown away
 * immediately after. We keep the requiredIf resolution inline instead —
 * any memoisation savings within one pass are dwarfed by the cost of
 * allocating and GC-ing a Map for ~60 controls. If a future schema
 * explodes to 10x controls AND profiling shows this hot, the per-call
 * memo can be added without touching the interface.
 *
 * ---- LB-001 fix-guard ---------------------------------------------------
 *
 * isRole24StaticallyRequired honours both `control.required === true` AND
 * an unconditional `validators: [{ name: 'required' }]` entry. The
 * component's isFieldRequired() today only inspects `control.required`, so
 * a schema edit that declares requiredness via the validators array alone
 * would be silently dropped editor-side. Role 24's current schemas don't
 * trip this (fields declare both or neither), so the guard is parity-neutral
 * today — see LB-009/10/11/12/13/16/17/18 in the latent-bugs ledger.
 */

function readCrossSheetFieldValue(
  context: SheetValidationContext,
  sheet: string,
  field: string
): unknown {
  const bundle = context.crossSheetData;
  if (!bundle) return undefined;
  const sheetPayload = bundle.get(sheet as Parameters<typeof bundle.get>[0]);
  if (!sheetPayload) return undefined;
  return sheetPayload[field];
}

/**
 * The dimensions the editor actually renders & validates for this filing —
 * both when Consolidated is visible, Separate-only when it is hidden.
 *
 * Delegates to the shared `resolveDimensionVisibility()` resolver (fed by the
 * central `getDimensionVisibilityConfig()` registry) so this headless path and
 * the mounted editor's `CrossReferenceService.getVisibility()` derive the gate
 * identically and cannot drift. The resolver applies the same prereq-first,
 * then-filingInfo precedence the role-local helper used to: it prefers the
 * `filingPrerequisites` slice (editor-side source of truth) and falls back to
 * `filingInfo`; an unknown/unset nature defaults to Consolidated visible — the
 * editor's pre-subscription posture. Role 24 has no period axis (CurrentYear
 * only), so `activeDimensions()` collapses to ['Separate'] for a Separate
 * filing and ['Consolidated', 'Separate'] otherwise.
 */
function resolveActiveDimensions(context: SheetValidationContext): ReadonlyArray<Role24Dimension> {
  const cells = resolveDimensionVisibility(
    getDimensionVisibilityConfig(SHEET_KEYS.notesRelatedPartyTransactions)!,
    {
      filingPrerequisites: readSlice(context.crossSheetData, 'filingPrerequisites'),
      filingInfo: readSlice(context.crossSheetData, 'filingInfo'),
    }
  );
  return activeDimensions(cells) as ReadonlyArray<Role24Dimension>;
}

/**
 * Does the control's cross-sheet requiredIf rule (if any) currently fire?
 * Returns false when there is no rule, when crossSheetData is absent, or
 * when the referenced value does not equal the configured literal.
 * Mirrors the component's computeFieldRequired() second branch.
 */
function crossSheetRequiredIfActive(
  control: Role24SchemaControl,
  context: SheetValidationContext
): boolean {
  const rule = control.requiredIf;
  if (!rule) return false;
  const value = readCrossSheetFieldValue(context, rule.sheet, rule.qname);
  return value === rule.equals;
}

/**
 * A control is effectively required when either the static (schema-level)
 * rule fires or the cross-sheet requiredIf rule fires. Mirrors the
 * component's computeFieldRequired() — short-circuits on static required
 * first (the fastest path).
 */
function isRole24Required(
  control: Role24SchemaControl,
  context: SheetValidationContext
): boolean {
  if (isRole24StaticallyRequired(control)) return true;
  return crossSheetRequiredIfActive(control, context);
}

function checkNonTableRequiredErrors(
  schema: Role24Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of schema.controls) {
    if (!isRole24ValidatingControl(control)) continue;
    if (control.ui?.tableColumn) continue; // handled separately
    if (!isRole24Required(control, context)) continue;
    if (isRole24Empty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }
  return errors;
}

function checkTableRowRequiredErrors(
  schema: Role24Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext,
  dimensions: ReadonlyArray<Role24Dimension>
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const control of schema.controls) {
    if (!isRole24ValidatingControl(control)) continue;
    if (!control.ui?.tableColumn) continue;
    if (!isRole24Required(control, context)) continue;

    for (const dimension of dimensions) {
      for (const key of buildRole24RowColumnKeys(control.qname, dimension)) {
        if (isRole24Empty(sheetData[key])) {
          errors[key] = `${key} is required`;
        }
      }
    }
  }
  return errors;
}

/**
 * Schema-driven column-sum reconciliation (Total related party = Σ 7 member
 * columns), replaying the mounted `ColumnSumValidationService.validateColumnSum`
 * exactly: per row × {Consolidated, Separate}, the Total cell must equal the
 * sum of its component cells (`parseFloat(...) || 0` coercion, 0.01 tolerance).
 *
 * Why this is replayed here (it was previously NOT): the editor folds a
 * column-sum mismatch into the row's `Failed` status (`hasColumnSumError` →
 * `evaluateSheetStatus` → `hasErrors`). "Total" is a user-entered column, so a
 * filled-but-imbalanced row (Total ≠ Σ members) is reachable from N8N
 * extraction or a manual edit with NO empty cell — the empty-required check
 * cannot catch it. Without replaying the arithmetic the conversion listing
 * (this validator) would show a green tick while the open sheet shows a red
 * cross. Honours the config's `appliesToRowQnames` so the headless check scopes
 * to the SAME rows as the mounted service.
 */
function checkColumnSumErrors(
  schema: Role24Schema,
  sheetData: Record<string, any>,
  dimensions: ReadonlyArray<Role24Dimension>
): Record<string, string> {
  const errors: Record<string, string> = {};
  const configs = schema.columnSumValidations;
  if (!configs || configs.length === 0) return errors;

  // Candidate rows: non-header `tableColumn` controls — matches the editor's
  // `controls.filter(c => c.ui.tableColumn && c.type !== 'SectionHeader')`.
  const rowControls = schema.controls.filter(
    (control) => control.ui?.tableColumn && control.type !== 'SectionHeader'
  );

  for (const config of configs) {
    const scopedRows =
      config.appliesToRowQnames && config.appliesToRowQnames.length > 0
        ? rowControls.filter((control) => config.appliesToRowQnames!.includes(control.qname))
        : rowControls;

    for (const control of scopedRows) {
      for (const dimension of dimensions) {
        let expectedSum = 0;
        for (const componentQname of config.componentColumnQnames) {
          const key = buildRole24ColumnKey(control.qname, componentQname, dimension);
          expectedSum += parseFloat(sheetData[key]) || 0;
        }

        const subTotalKey = buildRole24ColumnKey(
          control.qname,
          config.subTotalColumnQname,
          dimension
        );
        const actual = parseFloat(sheetData[subTotalKey]) || 0;

        if (differsAtCentPrecision(actual, expectedSum)) {
          errors[subTotalKey] =
            `${config.subTotalLabel} should equal to the sum of its related-party member columns`;
        }
      }
    }
  }
  return errors;
}

export const role24NotesRelatedPartyTransactionsValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.notesRelatedPartyTransactions,
  // Tier 4: cross-sheet requiredIf (no rule ships today — the MPERS
  // TypeOfCompany CONDITION was scrapped 2026-08-19, leaving a plain
  // `required: true`; the resolution stays for a future schema edit)
  // combined with virtual table-row expansion (16 flat keys per
  // schema row), PLUS the schema's columnSumValidations (Total = Σ 7 member
  // columns) replayed by checkColumnSumErrors() so the listing status matches
  // the editor's blocking `Failed` row status. See checkColumnSumErrors() for
  // why the empty-required check alone is insufficient.
  tier: 4,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = pickRole24Schema(context.accountingStandard);
    // Like role 23, hide (and stop validating) the Consolidated dimension when
    // the filing's Nature of Financial Statements is "Separate".
    const dimensions = resolveActiveDimensions(context);

    const errors: Record<string, string> = {
      ...checkNonTableRequiredErrors(schema, sheetData, context),
      ...checkTableRowRequiredErrors(schema, sheetData, context, dimensions),
      ...checkColumnSumErrors(schema, sheetData, dimensions),
    };

    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: false,
      errors,
    };
  },
};
